import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useNotifications } from './useNotifications';
import { customerService } from '../lib/services/customerService';
import { customerHistoryService } from '../lib/services/customerHistoryService';
import { Customer, CreateCustomerData, CustomerChangeHistory } from '../types/customer';
import toast from 'react-hot-toast';
import { collection, query, orderBy, getDocs, getDoc, doc, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerHistory, setCustomerHistory] = useState<Record<string, CustomerChangeHistory[]>>({});
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { createNotification } = useNotifications();

  const loadCustomers = useCallback(async () => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('customers')) {
      setLoading(false);
      return;
    }

    try {
      const customersRef = collection(db, 'customers');
      let q = query(customersRef, orderBy('createdAt', 'desc'));

      if (user?.role === "super_admin") {
        q = query(customersRef, orderBy('createdAt', 'desc'));
      } else if (user?.organizationId) {
        // Az ügyfelek organizations tömbben tárolják a szervezet ID-kat
        // array-contains operátorral keresünk azokat az ügyfeleket, 
        // akiknek organizations tömbje tartalmazza a felhasználó organizationId-ját
        q = query(customersRef, where('organizations', 'array-contains', user.organizationId));
      }

      const querySnapshot = await getDocs(q);
      
      const loadedCustomers = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Customer[];

      // Rendezés createdAt szerint csökkenő sorrendben (legújabb először)
      loadedCustomers.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const bDate = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return bDate - aDate;
      });

      setCustomers(loadedCustomers);
    } catch (err) {
      console.error('[useCustomers] Hiba az ügyfelek betöltése során:', err);
      setError(err as Error);
      toast.error(t('common.messages.customer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [user, hasAccess, t]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Load history for a specific customer
  const loadCustomerHistory = useCallback(async (customerId: string) => {
    if (!customerId) return [];
    
    try {
      setHistoryLoading(true);
      const history = await customerHistoryService.getCustomerHistory(customerId);
      
      setCustomerHistory(prev => ({
        ...prev,
        [customerId]: history
      }));
      
      return history;
    } catch (err) {
      console.error('Error loading customer history:', err);
      // Re-throw the error so it can be caught by the component
      throw err;
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const createCustomer = async (data: CreateCustomerData) => {
    if (!user) return;
    try {
      const id = await customerService.create(data, user.uid);
      await loadCustomers();

      // Értesítés létrehozása a létrehozónak
      try {
        if (user.organizationId) {
          // Nyelv alapú fordítások betöltése
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const customerName: string = data.type === 'organization' 
            ? (data.companyName || 'Szervezet')
            : `${data.firstName} ${data.lastName}`;
            
          const titleTemplate = (translations.subjects?.customerCreated || (lang === 'hu' ? 'Új ügyfél létrehozva: {{customerName}}' : 'New customer created: {{customerName}}')) as string;
          const title = titleTemplate.replace('{{customerName}}', customerName);
            
          await createNotification({
            type: 'system',
            title: title,
            message: `${customerName} ${lang === 'hu' ? 'ügyfél sikeresen létrehozva' : 'customer successfully created'}.`,
            priority: 'low',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/customers',
            data: { customerId: id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      return id;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  };

  const updateCustomer = async (id: string, data: Partial<Customer>) => {
    if (!user) return;
    try {
      await customerService.update(id, data, user.uid);
      await loadCustomers();
      
      // Refresh the history for this customer
      await loadCustomerHistory(id);

      // Értesítés létrehozása ha bármi változott
      try {
        if (user.organizationId) {
          // Nyelv alapú fordítások betöltése
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
          
          const customerName = data.companyName || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : 'Ügyfél');
          
          // Státusz változás
          if (data.status) {
            const statusLabel = translations.statuses[data.status] || data.status;
              
            const titleTemplate = (translations.subjects?.customerStatusChanged || (lang === 'hu' ? 'Ügyfél státusz változott: {{customerName}}' : 'Customer status changed: {{customerName}}')) as string;
            const title = titleTemplate.replace('{{customerName}}', customerName);
              
            await createNotification({
              type: 'customer',
              title: title,
              message: `${customerName} ${translations.fields?.status || (lang === 'hu' ? 'státusza' : 'status')}: ${statusLabel}`,
              priority: 'low',
              userId: user.id,
              organizationId: user.organizationId,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: '/customers',
              data: { customerId: id }
            });
          } 
          // Egyéb módosítás (név, email, cím, stb.)
          else {
            const changedFields = [];
            if (data.companyName) changedFields.push(translations.fields?.companyName || (lang === 'hu' ? 'cégnév' : 'company name'));
            if (data.firstName || data.lastName) changedFields.push(translations.fields?.name || (lang === 'hu' ? 'név' : 'name'));
            if (data.email) changedFields.push(translations.fields?.email || 'email');
            if (data.phone) changedFields.push(translations.fields?.phone || (lang === 'hu' ? 'telefon' : 'phone'));
            if (data.address) changedFields.push(translations.fields?.address || (lang === 'hu' ? 'cím' : 'address'));
            if (data.taxNumber) changedFields.push(translations.fields?.taxNumber || (lang === 'hu' ? 'adószám' : 'tax number'));
            if (data.notes) changedFields.push(translations.fields?.notes || (lang === 'hu' ? 'megjegyzés' : 'notes'));
            
            if (changedFields.length > 0) {
              const titleTemplate = (translations.subjects?.customerDataUpdated || (lang === 'hu' ? 'Ügyfél adatai módosultak: {{customerName}}' : 'Customer data updated: {{customerName}}')) as string;
              const title = titleTemplate.replace('{{customerName}}', customerName);
                
              await createNotification({
                type: 'customer',
                title: title,
                message: `${customerName} ${lang === 'hu' ? 'adatai frissültek' : 'data updated'}: ${changedFields.join(', ')}`,
                priority: 'low',
                userId: user.id,
                organizationId: user.organizationId,
                createdBy: user.id,
                createdByName: user.displayName,
                actionUrl: '/customers',
                data: { customerId: id }
              });
            }
          }
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    if (!user) return;
    try {
      await customerService.delete(id, user.uid);
      setCustomers(customers.filter(customer => customer.id !== id));
      
      // Remove history for deleted customer
      setCustomerHistory(prev => {
        const newHistory = { ...prev };
        delete newHistory[id];
        return newHistory;
      });
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  };

  const getCustomerById = useCallback(async (id: string) => {
    try {
      const customerDoc = await getDoc(doc(db, 'customers', id));
      if (customerDoc.exists()) {
        return { ...customerDoc.data(), id: customerDoc.id } as Customer;
      }
      return null;
    } catch (error) {
      console.error('Hiba az ügyfél lekérdezése során:', error);
      return null;
    }
  }, []);

  // Function to refresh a single customer by ID
  const refreshCustomer = useCallback(async (id: string) => {
    try {
      const refreshedCustomer = await getCustomerById(id);
      
      if (refreshedCustomer) {
        // Update the customers state by replacing the old customer with the fresh data
        setCustomers(currentCustomers => 
          currentCustomers.map(customer => 
            customer.id === id ? refreshedCustomer : customer
          )
        );
        return refreshedCustomer;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing customer:', error);
      throw error;
    }
  }, [getCustomerById]);

  return {
    customers,
    loading,
    error,
    customerHistory,
    historyLoading,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    loadCustomerHistory,
    refresh: loadCustomers,
    getCustomerById,
    refreshCustomer
  };
}; 